import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Wind, Waves, Sparkles, MapPin, Plane, Car, ShieldCheck, Radio, Users, Award,
  MessageCircle, ChevronRight, Star, Sun, Heart, Zap, Bot, ArrowRight, Check, Baby
} from "lucide-react";

const SPORTS = [
  {
    id: "kite",
    icon: "🪁",
    title: "Kitesurf",
    tagline: "Vola sopra l'acqua",
    blurb:
      "L'emozione di farsi trainare dal vento, controllando un'ala dal cielo. Con noi parti da zero, in sicurezza, con kite Hybrid (vola anche con poco vento) e radio in casco.",
    bullets: ["Acqua bassa in laguna", "Kite Hybrid: facile e sicuro", "Radio BBTalkin in casco", "Istruttori IKO"],
    grad: "from-cyan-400 to-blue-600",
    accent: "cyan",
  },
  {
    id: "wing",
    icon: "🪽",
    title: "Wingsurf & Wingfoil",
    tagline: "Lo sport che impari in un giorno",
    blurb:
      "Il nuovo sport dell'acqua: tavola, ala in mano, vento. In 2-3 ore impari le basi. Wingfoil dopo, per volare letteralmente sopra l'acqua.",
    bullets: ["1 giornata per partire", "Nessuna esperienza richiesta", "Perfetto in gruppo", "Adatto a tutte le età"],
    grad: "from-teal-400 to-emerald-600",
    accent: "teal",
  },
  {
    id: "wind",
    icon: "🌊",
    title: "Windsurf",
    tagline: "Il classico evergreen",
    blurb:
      "Tavola, vela e tanto divertimento. Lezioni solo per chi inizia: ti insegniamo tecnica corretta e ti facciamo provare subito la planata.",
    bullets: ["Solo lezioni principianti", "Bambini da 5 anni con Giocosurf", "Acqua piatta", "Attrezzatura nuova"],
    grad: "from-blue-400 to-indigo-600",
    accent: "blue",
  },
];

const REASONS = [
  { icon: Waves, title: "Laguna ad acqua bassa", desc: "L'acqua arriva alle ginocchia. Niente paura, niente onde. Il posto perfetto per imparare." },
  { icon: Wind, title: "Termico estivo affidabile", desc: "D'estate il vento arriva quasi ogni pomeriggio. Le tue lezioni non saltano mai per mancanza di vento." },
  { icon: Radio, title: "Radio in casco BBTalkin", desc: "Il maestro ti parla dall'altra parte dell'acqua. Impari più rapido e con meno stress." },
  { icon: ShieldCheck, title: "Kite Hybrid by Flysurfer", desc: "Vola con pochissimo vento, recupera in modo dolce. Lo strumento più sicuro per il tuo primo decollo." },
  { icon: Award, title: "Istruttori IKO certificati", desc: "Oltre 10 anni di esperienza. Metodi moderni, lezioni personalizzate, certificato internazionale opzionale (+€5)." },
  { icon: Heart, title: "Spiaggia dei cani", desc: "Vieni col tuo amico a 4 zampe. È una delle pochissime spiagge dog-friendly della Sardegna del Nord." },
];

const ACCESS = [
  { icon: Plane, title: "5 km dall'aeroporto", desc: "Atterri a Olbia, in 10 minuti sei in spiaggia." },
  { icon: Car, title: "Parcheggio gratuito", desc: "Posti auto direttamente sulla sabbia, senza pagare." },
  { icon: MapPin, title: "Saline di Olbia", desc: "Laguna riservata, niente folla, accesso facile." },
];

const FAQ = [
  {
    q: "Non ho mai fatto nessuno sport in acqua: ce la faccio?",
    a: "Sì, è proprio il nostro target. L'acqua bassa in laguna e l'attrezzatura giusta rendono il tuo primo giorno divertente, non stressante. Iniziamo da terra con il kite di trazione e in acqua quando sei pronto.",
  },
  {
    q: "Quanto tempo serve per fare una lezione di kitesurf?",
    a: "Il pacchetto base è 6 ore (divisibili in 2-3 giorni). In molti casi bastano per controllare il kite e fare i primi water-start. Per il wingsurf 2-3 ore sono già abbastanza per partire da soli.",
  },
  {
    q: "Quanto costa?",
    a: "Privato kitesurf da 2h: €130 (€65/h). Wingsurf privato 1h: €70. Wingsurf di gruppo da €40/persona. Bambini 7-14 anni: 1h privata a €65. Tutti i prezzi su /price o chiedi ad Agnese.",
  },
  {
    q: "E se non c'è vento?",
    a: "D'estate il termico è quasi sempre presente nel pomeriggio. Se proprio salta un giorno spostiamo la lezione su quello successivo, senza problemi.",
  },
  {
    q: "Posso prenotare per il mio bambino?",
    a: "Sì! Da 5-9 anni con Giocosurf/Windsurf (lezioni leggere e divertenti, 30 min). Da 7 anni anche kitesurf con sconto ragazzi €65/h.",
  },
  {
    q: "Devo portare la mia attrezzatura?",
    a: "No, è tutta inclusa: muta, kite, tavola, casco, radio. Tu porti solo costume e voglia di divertirti.",
  },
];

const REVIEWS = [
  { stars: 5, text: "Ottima esperienza di scuola con kitesurf e Wing foil, anche per bambini piccoli. Ottimi prezzi.", author: "Silvano D." },
  { stars: 5, text: "Gorgeous beach, great gear, very good communication from the school — highly recommend.", author: "Olya I." },
  { stars: 5, text: "Super école de kitesurf. Les moniteurs sont très compétents.", author: "Tracy F." },
  { stars: 5, text: "Fantastica prima lezione di wing, super paziente e spiega tutto nel dettaglio.", author: "Walter F." },
  { stars: 5, text: "Very good experience. The teachers took a lot of time to explain and correct. Made very good progress in short time.", author: "Arne H." },
  { stars: 5, text: "Amazing time with the team SKS! I took personal lesson and really liked this!", author: "AK" },
];

export default function InfoPage() {
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 antialiased selection:bg-cyan-200">
      {/* ==================== NAV ==================== */}
      <nav className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/85 backdrop-blur-xl shadow-sm border-b border-slate-200/60' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-between">
          <Link to="/info" className="flex items-center gap-2 group" data-testid="info-logo">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-600 flex items-center justify-center text-white font-black text-sm shadow-md group-hover:scale-110 transition">SKS</div>
            <span className={`font-bold text-sm ${scrolled ? 'text-slate-900' : 'text-white drop-shadow'}`}>Sardinia Kite School</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className={`hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition ${scrolled ? 'text-slate-700 hover:bg-slate-100' : 'text-white hover:bg-white/10'}`}
              data-testid="nav-agnese"
            >
              <Bot className="w-4 h-4" /> Chiedi ad Agnese
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold bg-gradient-to-r from-cyan-500 to-teal-600 text-white shadow-md hover:shadow-lg hover:scale-105 transition"
              data-testid="nav-book"
            >
              Prenota <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </nav>

      {/* ==================== HERO ==================== */}
      <section className="relative min-h-[100vh] flex items-center overflow-hidden">
        {/* Background ocean gradient + animated wave */}
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-600 via-teal-700 to-blue-900" />
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 -left-20 w-[600px] h-[600px] bg-cyan-300 rounded-full blur-[140px] animate-pulse" />
          <div className="absolute bottom-0 -right-20 w-[500px] h-[500px] bg-teal-300 rounded-full blur-[140px] animate-pulse" style={{ animationDelay: '1.5s' }} />
        </div>
        {/* SVG wave at bottom */}
        <svg className="absolute bottom-0 inset-x-0 text-slate-50" viewBox="0 0 1440 120" fill="currentColor" preserveAspectRatio="none">
          <path d="M0,64 C320,128 720,0 1440,64 L1440,120 L0,120 Z" />
        </svg>

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32 grid lg:grid-cols-12 gap-8 items-center w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="lg:col-span-7 text-white"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/20 mb-6">
              <Sparkles className="w-4 h-4 text-yellow-300" />
              <span className="text-xs font-semibold tracking-wide">Best school for beginners · 221+ recensioni 5★</span>
            </div>
            <h1 className="text-5xl sm:text-7xl lg:text-8xl font-black leading-[0.95] tracking-tighter mb-6" data-testid="hero-title">
              Impara a volare<br />
              <span className="bg-gradient-to-r from-cyan-200 via-yellow-200 to-emerald-200 bg-clip-text text-transparent">
                sull'acqua.
              </span>
            </h1>
            <p className="text-lg sm:text-2xl text-cyan-50/95 max-w-2xl leading-relaxed mb-8">
              Kitesurf, Wing, Windsurf. <strong>Da zero, in laguna, in sicurezza.</strong> A 5 km dall'aeroporto di Olbia, nel posto migliore della Sardegna per iniziare.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-slate-900 font-bold text-base hover:scale-105 transition shadow-xl"
                data-testid="hero-cta-book"
              >
                Prenota la prima lezione <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white/15 backdrop-blur-md border border-white/30 text-white font-bold text-base hover:bg-white/25 transition"
                data-testid="hero-cta-chat"
              >
                <MessageCircle className="w-5 h-5" /> Parla con Agnese
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-cyan-100/80">
              <div className="flex -space-x-2">
                {[1,2,3,4].map(i => (
                  <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-gradient-to-br from-cyan-300 to-teal-500 flex items-center justify-center text-xs font-bold text-white">{['L','M','A','S'][i-1]}</div>
                ))}
              </div>
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-yellow-300 text-yellow-300" />)}
                <span className="ml-2 font-semibold">221 recensioni</span>
              </div>
            </div>
          </motion.div>

          {/* Hero cards floating */}
          <motion.div
            initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-5 hidden lg:block"
          >
            <div className="relative h-[420px]">
              <div className="absolute top-0 left-12 w-64 rounded-2xl bg-white/95 backdrop-blur-xl p-5 shadow-2xl rotate-[-4deg] hover:rotate-0 transition">
                <div className="text-4xl mb-2">🪁</div>
                <h3 className="font-bold text-slate-900 text-lg">Kitesurf da zero</h3>
                <p className="text-sm text-slate-600 mt-1">2h privato</p>
                <p className="text-2xl font-black text-cyan-600 mt-2">€130</p>
              </div>
              <div className="absolute top-32 right-0 w-64 rounded-2xl bg-white/95 backdrop-blur-xl p-5 shadow-2xl rotate-[3deg] hover:rotate-0 transition">
                <div className="text-4xl mb-2">🪽</div>
                <h3 className="font-bold text-slate-900 text-lg">Wing in 1 giorno</h3>
                <p className="text-sm text-slate-600 mt-1">Facile e divertente</p>
                <p className="text-2xl font-black text-teal-600 mt-2">da €40/p</p>
              </div>
              <div className="absolute bottom-0 left-0 w-64 rounded-2xl bg-white/95 backdrop-blur-xl p-5 shadow-2xl rotate-[-2deg] hover:rotate-0 transition">
                <div className="text-4xl mb-2">🧒</div>
                <h3 className="font-bold text-slate-900 text-lg">Bimbi 5-9 anni</h3>
                <p className="text-sm text-slate-600 mt-1">Gioco-Surf 30 min</p>
                <p className="text-2xl font-black text-rose-600 mt-2">€30</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ==================== WHY US ==================== */}
      <section className="relative py-24 sm:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-16">
            <p className="text-cyan-600 font-bold text-sm tracking-widest uppercase mb-3">Perché scegliere SKS</p>
            <h2 className="text-4xl sm:text-6xl font-black tracking-tight leading-[1.05]">
              Sei principiante?<br />
              <span className="text-cyan-600">Allora questo è il tuo posto.</span>
            </h2>
            <p className="text-lg text-slate-600 mt-6 leading-relaxed">
              Tutto qui è pensato per chi inizia da zero: la laguna, gli strumenti, gli istruttori. Niente paura, niente confusione. Solo divertimento.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {REASONS.map((r, i) => (
              <motion.div
                key={r.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="group p-7 rounded-3xl bg-white border border-slate-200 hover:border-cyan-300 hover:shadow-xl hover:shadow-cyan-100/50 transition-all"
              >
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-500 to-teal-600 text-white flex items-center justify-center mb-5 group-hover:scale-110 group-hover:rotate-3 transition">
                  <r.icon className="w-6 h-6" />
                </div>
                <h3 className="font-black text-xl mb-2">{r.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{r.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== SPORTS ==================== */}
      <section className="relative py-24 sm:py-32 bg-gradient-to-b from-slate-50 to-slate-100">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-16">
            <p className="text-cyan-600 font-bold text-sm tracking-widest uppercase mb-3">I nostri sport</p>
            <h2 className="text-4xl sm:text-6xl font-black tracking-tight leading-[1.05]">
              3 sport. <span className="text-cyan-600">1 spiaggia.</span><br />
              0 esperienza richiesta.
            </h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {SPORTS.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group relative overflow-hidden rounded-3xl bg-white border border-slate-200 hover:shadow-2xl transition-all"
              >
                <div className={`relative h-40 bg-gradient-to-br ${s.grad} overflow-hidden`}>
                  <div className="absolute inset-0 flex items-center justify-center text-7xl">{s.icon}</div>
                  <div className="absolute -top-12 -right-12 w-40 h-40 bg-white/20 rounded-full blur-2xl" />
                </div>
                <div className="p-7">
                  <h3 className="text-3xl font-black mb-1">{s.title}</h3>
                  <p className={`text-sm font-bold text-${s.accent}-600 mb-4 uppercase tracking-wide`}>{s.tagline}</p>
                  <p className="text-slate-600 text-sm leading-relaxed mb-5">{s.blurb}</p>
                  <ul className="space-y-2 mb-6">
                    {s.bullets.map(b => (
                      <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check className={`w-4 h-4 text-${s.accent}-600 mt-0.5 shrink-0`} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/"
                    className={`inline-flex items-center gap-1.5 text-sm font-bold text-${s.accent}-600 hover:gap-2.5 transition-all`}
                    data-testid={`sport-cta-${s.id}`}
                  >
                    Prenota {s.title} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== ACCESS / LOCATION ==================== */}
      <section className="py-24 sm:py-32 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-cyan-500 rounded-full blur-[180px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-cyan-300 font-bold text-sm tracking-widest uppercase mb-3">Dove siamo</p>
            <h2 className="text-4xl sm:text-6xl font-black leading-[1.05] mb-6">
              Saline di Olbia.<br />
              <span className="bg-gradient-to-r from-cyan-300 to-emerald-300 bg-clip-text text-transparent">Sardegna del Nord.</span>
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-8">
              Una laguna riservata ad acqua bassa, a 5 km dall'aeroporto. Spiaggia non affollata, parcheggio gratuito, dog-friendly. Il setup perfetto per imparare senza stress.
            </p>
            <div className="grid sm:grid-cols-3 gap-3">
              {ACCESS.map(a => (
                <div key={a.title} className="p-5 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10">
                  <a.icon className="w-6 h-6 text-cyan-300 mb-3" />
                  <h4 className="font-bold mb-1">{a.title}</h4>
                  <p className="text-xs text-slate-400">{a.desc}</p>
                </div>
              ))}
            </div>
            <a
              href="https://maps.app.goo.gl/VPjSF6WYjZH5QqBJ9"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-full bg-white text-slate-900 font-bold hover:scale-105 transition"
              data-testid="info-maps-link"
            >
              <MapPin className="w-5 h-5" /> Apri in Google Maps
            </a>
          </div>
          <div className="relative h-[400px] rounded-3xl overflow-hidden border-2 border-white/20 shadow-2xl">
            <iframe
              title="SKS location"
              src="https://www.google.com/maps?q=Saline+Olbia&output=embed"
              width="100%" height="100%"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* ==================== REVIEWS ==================== */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-1 mb-4">
              {[1,2,3,4,5].map(i => <Star key={i} className="w-7 h-7 fill-yellow-400 text-yellow-400" />)}
            </div>
            <p className="text-2xl font-black mb-1">EXCELLENT · 4.9/5</p>
            <p className="text-slate-600">Su 221 recensioni Google</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {REVIEWS.map((r, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="p-6 rounded-2xl bg-slate-50 border border-slate-200 hover:border-slate-300 transition"
              >
                <div className="flex gap-0.5 mb-3">
                  {Array(r.stars).fill(0).map((_, j) => <Star key={j} className="w-4 h-4 fill-yellow-400 text-yellow-400" />)}
                </div>
                <p className="text-slate-700 text-sm leading-relaxed mb-4">"{r.text}"</p>
                <p className="text-xs font-bold text-slate-500">— {r.author}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== FAQ ==================== */}
      <section className="py-24 sm:py-32 bg-slate-50">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-14">
            <p className="text-cyan-600 font-bold text-sm tracking-widest uppercase mb-3">Domande frequenti</p>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tight">Tutto quello che vuoi sapere</h2>
            <p className="text-slate-600 mt-4">Hai altre domande? <Link to="/" className="text-cyan-600 font-bold underline">Chiedi ad Agnese →</Link></p>
          </div>
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <div
                key={i}
                className="rounded-2xl bg-white border border-slate-200 overflow-hidden"
                data-testid={`faq-item-${i}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-slate-50 transition"
                >
                  <span className="font-bold text-base">{f.q}</span>
                  <ChevronRight className={`w-5 h-5 text-slate-400 transition shrink-0 ${openFaq === i ? 'rotate-90' : ''}`} />
                </button>
                {openFaq === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    className="px-5 pb-5 text-slate-600 text-sm leading-relaxed"
                  >
                    {f.a}
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== FINAL CTA ==================== */}
      <section className="relative py-24 sm:py-32 overflow-hidden bg-gradient-to-br from-cyan-600 via-teal-700 to-blue-900 text-white">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 right-1/4 w-[600px] h-[600px] bg-yellow-300 rounded-full blur-[180px]" />
        </div>
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <Bot className="w-16 h-16 mx-auto mb-6 text-yellow-300" />
          <h2 className="text-4xl sm:text-6xl font-black mb-6 leading-tight">
            Non sai da dove iniziare?<br />
            <span className="bg-gradient-to-r from-yellow-200 to-cyan-200 bg-clip-text text-transparent">Chiedi ad Agnese.</span>
          </h2>
          <p className="text-lg text-cyan-50/95 max-w-2xl mx-auto mb-10 leading-relaxed">
            La nostra assistente AI ti aiuta a scegliere lo sport giusto, ti dice quando c'è vento, ti spiega i prezzi e prenota la lezione per te. In italiano, inglese, francese, tedesco e spagnolo.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-slate-900 font-bold text-base hover:scale-105 transition shadow-xl"
              data-testid="cta-final-chat"
            >
              <MessageCircle className="w-5 h-5" /> Apri la chat con Agnese
            </Link>
            <a
              href="https://wa.me/393513590924"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-green-500 text-white font-bold text-base hover:scale-105 transition shadow-xl"
              data-testid="cta-final-whatsapp"
            >
              <Zap className="w-5 h-5" /> WhatsApp diretto
            </a>
          </div>
        </div>
      </section>

      {/* ==================== FOOTER ==================== */}
      <footer className="bg-slate-950 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-600 flex items-center justify-center text-white font-black text-sm">SKS</div>
              <span className="font-bold text-white text-sm">Sardinia Kite School</span>
            </div>
            <p className="text-xs leading-relaxed">Move Sardinia A.S.D.<br />Saline di Olbia · Sardegna</p>
          </div>
          <div>
            <h4 className="text-white font-bold mb-3 text-sm">Sport</h4>
            <ul className="space-y-2 text-xs">
              <li><Link to="/" className="hover:text-cyan-300 transition">Kitesurf</Link></li>
              <li><Link to="/" className="hover:text-cyan-300 transition">Wingsurf/Wingfoil</Link></li>
              <li><Link to="/" className="hover:text-cyan-300 transition">Windsurf</Link></li>
              <li><Link to="/" className="hover:text-cyan-300 transition">Per bambini</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-bold mb-3 text-sm">Link utili</h4>
            <ul className="space-y-2 text-xs">
              <li><Link to="/" className="hover:text-cyan-300 transition">Chat Agnese</Link></li>
              <li><Link to="/member" className="hover:text-cyan-300 transition">Iscrizione diretta</Link></li>
              <li><a href="https://sardiniakiteschool.net" className="hover:text-cyan-300 transition">Sito ufficiale</a></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-bold mb-3 text-sm">Contatti</h4>
            <ul className="space-y-2 text-xs">
              <li><a href="https://wa.me/393513590924" className="hover:text-green-300 transition">WhatsApp: +39 351 359 0924</a></li>
              <li><a href="https://maps.app.goo.gl/VPjSF6WYjZH5QqBJ9" className="hover:text-cyan-300 transition">Vedi su Mappa</a></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-10 pt-6 border-t border-slate-800 text-xs text-center text-slate-500">
          © {new Date().getFullYear()} Sardinia Kite School · Move Sardinia A.S.D.
        </div>
      </footer>

      {/* ==================== FLOATING AGNESE CTA ==================== */}
      <Link
        to="/"
        className="fixed bottom-6 right-6 z-40 group flex items-center gap-3 px-5 py-3.5 rounded-full bg-gradient-to-r from-cyan-500 to-teal-600 text-white font-bold shadow-2xl shadow-cyan-500/40 hover:scale-105 transition"
        data-testid="floating-agnese-cta"
      >
        <div className="relative">
          <Bot className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-ping" />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full" />
        </div>
        <span className="hidden sm:inline">Chiedi ad Agnese</span>
      </Link>
    </div>
  );
}
